import { Dispatch, createContext, useContext, useMemo } from 'react';
import { useImmerReducer } from 'use-immer';
import {
  GlobalLayoutAction,
  GlobalLayoutState,
  globalInitialState,
  globalReducer,
} from './globalSetting';
import useSetIframe, { Iframe } from '@vindr/utils/hooks/useSetIframe';

export interface GlobalLayoutContextProps {
  iframe: Iframe;
  globalSetting: GlobalLayoutState;
  dispatchGlobalLayoutSetting: Dispatch<GlobalLayoutAction>;
}

const GlobalLayoutStateContext = createContext<GlobalLayoutContextProps>(
  {} as GlobalLayoutContextProps
);
const GlobalLayoutUpdaterContext = createContext<Dispatch<GlobalLayoutAction>>(null);
export const GlobalLayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [globalSetting, dispatchGlobalLayoutSetting] = useImmerReducer(
    globalReducer,
    globalInitialState
  );
  const [iframeState, setOpenIframe, setCloseIframe, setToggleIframe] = useSetIframe();

  const memorizedValues: GlobalLayoutContextProps = useMemo(
    () => ({
      iframe: [iframeState, setOpenIframe, setCloseIframe, setToggleIframe],
      globalSetting,
      dispatchGlobalLayoutSetting,
    }),
    [
      iframeState,
      setOpenIframe,
      setCloseIframe,
      setToggleIframe,
      globalSetting,
      dispatchGlobalLayoutSetting,
    ]
  );

  return (
    <GlobalLayoutStateContext.Provider value={memorizedValues}>
      <GlobalLayoutUpdaterContext.Provider value={dispatchGlobalLayoutSetting}>
        {children}
      </GlobalLayoutUpdaterContext.Provider>
    </GlobalLayoutStateContext.Provider>
  );
};

export default GlobalLayoutProvider;

export const useGlobalLayoutState = () => {
  const context = useContext(GlobalLayoutStateContext);

  if (!context)
    throw new Error('useGlobalLayoutState must be used within a GlobalLayoutStateContext');

  return context;
};

export const useGlobalLayoutUpdater = () => {
  const context = useContext(GlobalLayoutUpdaterContext);

  if (!context)
    throw new Error('useGlobalLayoutUpdater must be used within a GlobalLayoutUpdaterContext');

  return context;
};

export const useGlobalLayoutContext = () => [
  useContext(GlobalLayoutStateContext),
  useContext(GlobalLayoutUpdaterContext),
];
